import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import { useSetRecoilState } from 'recoil';
import { ProductCategory, } from '@zurich/api/claim';
import { LocaleContext, } from '@zurich/common/contexts';
import { useTranslation } from '@zurich/common/hooks';
import { localPartyClaimNoAtom } from '@zurich/party/recoil';
import { Button, DataTableRow, DataTableRowContent, DataTableRowFooter, DataTableRowHeader, Tag, Typography, } from '@zurich/ui/legacy/components';
import { useAppLink, useAppLinkUi } from '@zurich/ui/legacy/hooks';
import { styled } from '@zurich/ui/legacy/utils';
import { ClaimLegacyDescription, ClaimZdbStatus, } from '@zurich-client/typeutils/claim';
import { useGetOfferIdByPolicyNo } from '../../hooks';
export const getClaimStatusTagVariant = (claimStatus) => {
    switch (claimStatus) {
        case 'W':
            return 'purple';
        case 'O': // Case 'O' stands for 'offen'
            return 'orange';
        case 'G': // Case 'G' stands for 'abgeschlossen'/'erledigt'
            return 'green';
        case 'E':
            return 'orange';
        default:
            return 'grey';
    }
};
const PolicyLink = ({ policyNo, linkPrefix }) => {
    const AppLink = useAppLinkUi();
    const { data: offerId } = useGetOfferIdByPolicyNo(policyNo || '');
    return (_jsx(AppLink, { href: `${linkPrefix ?? ''}/contract/${offerId}`, children: policyNo }));
};
export const getProductCategoryIcon = (productCategory) => {
    switch (productCategory) {
        case 'Motor':
            return 'drive2';
        case 'Life':
            return 'hand-heart';
        case 'NonMotor':
            return 'trust';
        default:
            return 'documents';
    }
};
const getData = (claim, t, locale, simpleView, linkPrefix) => {
    const { formatDate, formatMoneyAmount } = locale;
    const data = [
        simpleView
            ? null
            : {
                caption: t('claim.list.eventDate'),
                data: [claim.eventDate && formatDate(claim.eventDate)],
            },
        simpleView
            ? {
                caption: t('claim.list.policyNo'),
                data: [
                    _jsx(PolicyLink, { linkPrefix: linkPrefix, policyNo: claim.policyNo }, claim.policyNo),
                ],
            }
            : null,
        simpleView
            ? null
            : {
                caption: t('claim.list.notificationDate'),
                data: [claim.notificationDate && formatDate(claim.notificationDate)],
            },
        simpleView
            ? {
                caption: t('claim.list.claimNumber'),
                data: [claim.claimNumber],
            }
            : null,
        {
            caption: t('claim.list.amount'),
            data: [formatMoneyAmount(claim.amount)],
        },
        {
            caption: t('claim.list.chlaimHandler'),
            data: [claim.claimHandler],
        },
        simpleView
            ? null
            : {
                caption: t('claim.list.causeOfDamage'),
                data: [
                    ClaimLegacyDescription.getTranslatedValue(claim.causeOfdamage, t),
                ],
            },
        claim.productCategory === ProductCategory.MOTOR && !simpleView
            ? {
                caption: t('claim.list.damageLicensePlate'),
                data: [claim.damageLicensePlate],
            }
            : null,
        simpleView
            ? null
            : {
                caption: t('claim.list.bmsw'),
                data: [claim.bmsw],
            },
        simpleView
            ? null
            : {
                caption: t('claim.list.status'),
                data: [claim.status],
            },
    ];
    return data.filter((item) => item !== null);
};
export function PortalClaimOverview({ claim, linkPrefix, simpleView, }) {
    const locale = useContext(LocaleContext);
    const { formatDate } = locale;
    const { t } = useTranslation();
    const AppLink = useAppLink();
    const data = getData(claim, t, locale, simpleView, linkPrefix);
    const setLocalClaimNo = useSetRecoilState(localPartyClaimNoAtom);
    function handleClick() {
        claim.claimNumber && setLocalClaimNo(claim.claimNumber);
    }
    return (_jsxs(DataTableRow, { paddingMobile: 0, rowGap: 3, children: [_jsx(DataTableRowHeader, { title: _jsx(AppLink, { href: `${linkPrefix ?? ''}/claim/${claim.claimNumber}`, children: _jsxs(TypographyStyled, { variant: "subtitle1", children: [simpleView ? (`Ereignis vom ${claim.eventDate && formatDate(claim.eventDate)} -`) : (_jsx("strong", { children: claim.claimNumber })), "\u00A0", claim.productName ?? ''] }) }), tags: _jsx(_Fragment, { children: claim.status && (_jsx(Tag, { variantTypo: "body2", variant: getClaimStatusTagVariant(claim.status), children: ClaimZdbStatus.getTranslatedValue(claim.status, t).toLowerCase() })) }), editable: false }), _jsx(DataTableRowContent, { data: data, maxColumns: 9, rowGap: 1, columnGap: 1 }), _jsxs(DataTableRowFooter, { children: [_jsx("div", {}), _jsx(AppLink, { href: `${linkPrefix ?? ''}/claim/${claim.claimNumber}`, children: _jsx(Button, { variant: "outlined", onClick: handleClick, children: t('claim.linkToDetailPage') }) })] })] }));
}
const TypographyStyled = styled(Typography)({
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
});
